import * as React from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../services/api-client';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Navigate } from 'react-router-dom';
import AppConfig from '../services/app-config';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import ShareIcon from '@mui/icons-material/Share';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import QRCodeStyling from 'qr-code-styling';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import AuthService from '../services/auth';
import { orgLogoUrl } from '../components/OrgLogo';
import { NEXA_FONT } from '../theme';

export default function ShareCode({setNavbarState, navbarState}) {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState('');
    const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
    const [accessDetails, setAccessDetails] = React.useState(null);
    const [passPreviewHeight, setPassPreviewHeight] = React.useState(null);
    const [passPreviewOffsetY, setPassPreviewOffsetY] = React.useState(0);
    const [passFullDisplayHeight, setPassFullDisplayHeight] = React.useState(null);
    const passCanvasRef = React.useRef(null);

    const publicUrl = process.env.REACT_APP_PUBLIC_URL;

    const qrValue = React.useMemo(() => {
        if (!code) return '';
        if (!publicUrl) return code;
        return `${publicUrl}/recepcion?codigo=${code}`;
    }, [code, publicUrl]);

    
    const deactivateCode = async () => {
        setOpenConfirmDialog(false);
        setLoading(true);
        try {
            const response = await apiClient.post('visit/invalidate', { access_code: code }, {}, { action: 'invalidate_visit' });
            AppConfig.parseResponse(response);
            if (response.status === 200) {
                setRedirect({ trigger: true, route: '/visitas' });
            }
        } catch (error) {
            setRedirect({ trigger: true, route: '/visitas' });
            console.log(error);
        }
        setLoading(false);
    };

    const loadImage = React.useCallback((src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = src;
        });
    }, []);

    const drawCenteredText = React.useCallback((ctx, text, y, font, color) => {
        ctx.save();
        ctx.font = font;
        ctx.fillStyle = color;
        ctx.textBaseline = 'top';
        const metrics = ctx.measureText(text);
        const x = (ctx.canvas.width - metrics.width) / 2;
        ctx.fillText(text, x, y);
        ctx.restore();
    }, []);

    const drawCenteredTextWithSpacing = React.useCallback((ctx, text, y, font, color, letterSpacingPx) => {
        const t = String(text || '');
        if (!t) return;

        ctx.save();
        ctx.font = font;
        ctx.fillStyle = color;
        ctx.textBaseline = 'top';

        const chars = Array.from(t);
        let totalWidth = 0;
        for (let i = 0; i < chars.length; i += 1) {
            totalWidth += ctx.measureText(chars[i]).width;
            if (i !== chars.length - 1) totalWidth += letterSpacingPx;
        }

        let x = (ctx.canvas.width - totalWidth) / 2;
        for (let i = 0; i < chars.length; i += 1) {
            const ch = chars[i];
            ctx.fillText(ch, x, y);
            x += ctx.measureText(ch).width + (i !== chars.length - 1 ? letterSpacingPx : 0);
        }

        ctx.restore();
        return totalWidth;
    }, []);

    const drawRoundedRect = React.useCallback((ctx, x, y, w, h, r, fillStyle, strokeStyle) => {
        ctx.save();
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(x, y, w, h, r);
        } else {
            const radius = Math.min(r, w / 2, h / 2);
            ctx.moveTo(x + radius, y);
            ctx.arcTo(x + w, y, x + w, y + h, radius);
            ctx.arcTo(x + w, y + h, x, y + h, radius);
            ctx.arcTo(x, y + h, x, y, radius);
            ctx.arcTo(x, y, x + w, y, radius);
        }
        if (fillStyle) {
            ctx.fillStyle = fillStyle;
            ctx.fill();
        }
        if (strokeStyle) {
            ctx.strokeStyle = strokeStyle;
            ctx.lineWidth = 2;
            ctx.stroke();
        }
        ctx.restore();
    }, []);

    const renderPassToCanvas = React.useCallback(async () => {
        const canvas = passCanvasRef.current;
        if (!canvas) return;
        if (!code) return;
        if (!accessDetails) return;
        if (!qrValue) return;

        const orgColor = AppConfig.get('color') || '#133B43';
        const unitType = AppConfig.get('unit_type') || 'Unidad';
        const orgDisplayName = AuthService.getCurrentUserOrg() || '';
        const orgSlug = (orgDisplayName || '').toLowerCase().replace(/\s+/g, '');

        const isVehicle = !!accessDetails?.vehicle;
        const qrOnWalking = AppConfig.getf('qr_on_walking', true);
        const qrOnVehicle = AppConfig.getf('qr_on_vehicle', true);
        const createQr = isVehicle ? !!qrOnVehicle : !!qrOnWalking;

        const width = 800;
        const height = createQr ? 1450 : 1200;
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);

        // Top highlight bar
        ctx.fillStyle = orgColor;
        ctx.fillRect(0, 0, width, 100);

        // Org logo
        const logoOrgKey = AuthService.getCurrentUserOrgKey() || orgSlug;
        const logoUrls = [
            orgLogoUrl(logoOrgKey, 'original'),
            `/orgs/${orgSlug}.png`,
        ];
        let logo = null;
        for (const url of logoUrls) {
            try { logo = await loadImage(url); break; } catch (e) { /* try next */ }
        }
        if (logo) {
            const maxLogoWidth = 200;
            const maxLogoHeight = 100;
            let logoW = logo.width;
            let logoH = logo.height;
            const scaleW = maxLogoWidth / logoW;
            const scaleH = maxLogoHeight / logoH;
            const scale = Math.min(scaleW, scaleH, 1);
            logoW = Math.round(logoW * scale);
            logoH = Math.round(logoH * scale);
            const x = (width - logoW) / 2;
            const y = 200;
            ctx.drawImage(logo, x, y, logoW, logoH);
        }

        // Headline and org name (match backend positions)
        drawCenteredText(ctx, 'Bienvenido a', 20 + 300, '42px Lexend, sans-serif', '#000000');
        drawCenteredText(ctx, (orgDisplayName || '').trim() || ' ', 60 + 300, '47px Lexend, sans-serif', '#000000');

        // Visitor name
        const name = (accessDetails?.visitor_name || accessDetails?.nombre || '').trim();
        const nameFont = '42px Lexend, sans-serif';
        drawCenteredText(ctx, name || code, 180 + 300, nameFont, '#000000');

        if (createQr) {
            // Styled QR
            const qr = new QRCodeStyling({
                width: 550,
                height: 550,
                type: 'canvas',
                data: qrValue,
                margin: 0,
                qrOptions: {
                    errorCorrectionLevel: 'H',
                },
                dotsOptions: {
                    color: '#000000',
                    type: 'rounded',
                },
                cornersSquareOptions: {
                    color: '#000000',
                    type: 'extra-rounded',
                },
                cornersDotOptions: {
                    color: '#000000',
                    type: 'dot',
                },
                backgroundOptions: {
                    color: '#FFFFFF',
                },
            });

            const qrNode = await qr.getRawData('png');
            const qrUrl = URL.createObjectURL(qrNode);
            const qrSize = 550;
            const qrX = (width - qrSize) / 2;
            const qrY = 250 + 300;
            try {
                const qrImg = await loadImage(qrUrl);
                ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
            } finally {
                URL.revokeObjectURL(qrUrl);
            }

            // Org-colored center plate with logo (QR uses error correction H, keep plate <= 20% of QR)
            if (logo) {
                const plateSize = Math.round(qrSize * 0.2);
                const plateX = qrX + (qrSize - plateSize) / 2;
                const plateY = qrY + (qrSize - plateSize) / 2;
                drawRoundedRect(ctx, plateX, plateY, plateSize, plateSize, Math.round(plateSize * 0.22), orgColor, '#FFFFFF');
                const platePad = Math.round(plateSize * 0.18);
                const plateLogoMax = plateSize - platePad * 2;
                const plateScale = Math.min(plateLogoMax / logo.width, plateLogoMax / logo.height, 1);
                const plateLogoW = Math.round(logo.width * plateScale);
                const plateLogoH = Math.round(logo.height * plateScale);
                ctx.drawImage(logo, plateX + (plateSize - plateLogoW) / 2, plateY + (plateSize - plateLogoH) / 2, plateLogoW, plateLogoH);
            }

            // Code under QR (add extra spacing to avoid overlap)
            const codeY = qrY + qrSize + 20;
            drawCenteredText(ctx, code, codeY, '42px Lexend, sans-serif', '#808080');

            // Unit footer
            const unit = (accessDetails?.unit || '').trim();
            drawCenteredText(ctx, `${unitType} ${unit || '—'}`.trim(), codeY + 75, '42px Lexend, sans-serif', '#000000');
        } else {
            // Render code-only pass (rounded box) like backend
            const accessCodeLabel = 'Codigo de acceso';
            const unit = (accessDetails?.unit || '').trim();
            const bigCodeLetterSpacing = 8;

            ctx.save();
            ctx.font = '42px Lexend, sans-serif';
            ctx.textBaseline = 'top';
            const labelWidth = ctx.measureText(accessCodeLabel).width;
            const labelY = 300 + 300;
            const rectY = labelY - 30;
            const rectH = 200;

            // Compute spaced code width (same font as the big code)
            ctx.font = '600 100px Lexend, sans-serif';
            const codeChars = Array.from(String(code || ''));
            let spacedCodeWidth = 0;
            for (let i = 0; i < codeChars.length; i += 1) {
                spacedCodeWidth += ctx.measureText(codeChars[i]).width;
                if (i !== codeChars.length - 1) spacedCodeWidth += bigCodeLetterSpacing;
            }

            // Make the box wide enough for both label and spaced code
            const contentWidth = Math.max(labelWidth, spacedCodeWidth);
            const rectW = contentWidth + 300;
            const rectX = (width - rectW) / 2;

            // Label x after rect is computed
            ctx.font = '42px Lexend, sans-serif';
            const labelX = (width - labelWidth) / 2;

            drawRoundedRect(ctx, rectX, rectY, rectW, rectH, 20, '#FFFFFF', '#000000');
            ctx.fillStyle = '#000000';
            ctx.fillText(accessCodeLabel, labelX, labelY);
            ctx.restore();

            // Big code
            const bigCodeY = 340 + 300;
            drawCenteredTextWithSpacing(ctx, code, bigCodeY, '600 100px Lexend, sans-serif', '#000000', bigCodeLetterSpacing);

            // Unit footer (centered between code box and footer)
            const boxBottomY = rectY + rectH;
            const footerY = height - 45;
            const unitFontSize = 42;
            const unitY = boxBottomY + Math.max(0, (footerY - boxBottomY - unitFontSize) / 2);
            drawCenteredText(ctx, `${unitType} ${unit || '—'}`.trim(), unitY, '42px Lexend, sans-serif', '#000000');
        }

        // Preview crop values (visual-only crop; keep full canvas for share/download)
        const cropTop = createQr ? 430 : 420;
        const cropBottom = 0;
        const cropHeight = Math.max(1, height - cropTop - cropBottom);

        if (width > 0 && height > 0 && cropHeight > 0) {
            const previewWidth = 260;
            const scale = previewWidth / width;
            setPassPreviewHeight(Math.round(cropHeight * scale) - 30);
            setPassPreviewOffsetY(Math.round(cropTop * scale));
            setPassFullDisplayHeight(Math.round(height * scale) - 30);
        }
    }, [accessDetails, code, drawCenteredText, drawCenteredTextWithSpacing, drawRoundedRect, loadImage, qrValue]);

    const fetchAccessDetails = async () => {
        try {
            const response = await apiClient.get(`visit/details/${code}`, {}, { action: 'fetch_visit_details' });
            AppConfig.parseResponse(response);
            if (response.status === 200) {
                setAccessDetails(response.data.data);
            } else {
                setLoadError('No pudimos cargar los detalles del acceso.');
            }
        } catch (error) {
            console.log(error);
            setLoadError('No pudimos cargar los detalles del acceso.');
        }
    };

    const getCanvasBlob = async () => {
        const canvas = passCanvasRef.current;
        if (!canvas) return null;
        const blob = await new Promise((resolve) => {
            canvas.toBlob((b) => resolve(b), 'image/png');
        });
        return blob;
    };

    const downloadPass = async () => {
        const blob = await getCanvasBlob();
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Acceso_${code}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const shareImage = async () => {
        const blob = await getCanvasBlob();
        if (!blob) return;
        if (!navigator.share) return;
        try {
            await navigator.share({
                files: [
                    new File([blob], `Acceso_${code}.png`, { type: blob.type }),
                ]
            });
        } catch (err) {
            console.error(err);
        }
    };

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            await fetchAccessDetails();
            setLoading(false);
        };
        load();
        setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false});
    }, []);

    React.useEffect(() => {
        // noop
    }, [code]);

    React.useEffect(() => {
        renderPassToCanvas();
    }, [renderPassToCanvas]);

    const validFrom = accessDetails?.valid_from || accessDetails?.ts;
    const validUntil = accessDetails?.valid_until || accessDetails?.valid_thru;

    const validityLabel = React.useMemo(() => {
        const from = Number(validFrom);
        const until = Number(validUntil);
        if (!Number.isFinite(from) || !Number.isFinite(until) || until <= from) return null;
        const duration = until - from;
        if (duration % 86400 === 0) {
            const days = duration / 86400;
            return days === 1 ? 'Válido 1 día' : `Válido ${days} días`;
        }
        if (duration >= 3600) {
            return `Válido ${Math.round(duration / 3600)} horas`;
        }
        return `Válido ${Math.max(1, Math.round(duration / 60))} min`;
    }, [validFrom, validUntil]);

    return (
        <Box sx={{ position: 'relative', minHeight: '100vh', bgcolor: 'background.default' }}>
            {redirect.trigger && (
                <Navigate to={redirect.route} replace={true} />
            )}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {!loading && (
                <>
                    <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.default', backdropFilter: 'blur(8px)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2, pb: 1, position: 'relative' }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: NEXA_FONT, fontSize: '1rem', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', whiteSpace: 'nowrap' }}>
                                Compartir Invitación
                            </Typography>
                            <Button
                                onClick={() => setRedirect({ trigger: true, route: '/visitas' })}
                                color="inherit"
                                sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '999px' }}
                            >
                                <CloseIcon />
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ px: 2, pb: 14 }}>
                        {loadError && (
                            <Alert severity="error" sx={{ maxWidth: 420, mx: 'auto', mt: 2 }}>
                                {loadError}
                            </Alert>
                        )}
                        <Box sx={{ maxWidth: 420, mx: 'auto', pt: 2, display: loadError ? 'none' : 'block' }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: '10px',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: (theme) => theme.shadows[3],
                                    p: 3,
                                    textAlign: 'center'
                                }}
                            >
                                {validityLabel && (
                                    <Box sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1.31,
                                        py: 0.79,
                                        borderRadius: '10px',
                                        fontSize: '10.5px',
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(125, 217, 158, 0.22)' : 'rgba(125, 217, 158, 0.50)',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#7DD99E' : '#238048',
                                        mb: 3,
                                        gap: 0.5,
                                    }}>
                                        <VerifiedIcon sx={{ fontSize: 14 }} />
                                        {validityLabel}
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                    <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 2, display: 'inline-flex' }}>
                                        <Box
                                            sx={{
                                                width: 260,
                                                height: passPreviewHeight || 440,
                                                overflow: 'hidden',
                                                borderRadius: 2,
                                                bgcolor: 'white'
                                            }}
                                        >
                                            <Box
                                                component="canvas"
                                                ref={passCanvasRef}
                                                sx={{
                                                    width: 260,
                                                    height: passFullDisplayHeight || 440,
                                                    display: 'block',
                                                    transform: `translateY(-${passPreviewOffsetY}px)`,
                                                    transformOrigin: 'top left',
                                                    bgcolor: 'white'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                            </Paper>

                            <Stack spacing={1.5} sx={{ mt: 2, px: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                        Ingreso
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                        {validFrom ? AppConfig.formatFullDate(validFrom) : '—'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                        Vence
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                        {validUntil ? AppConfig.formatFullDate(validUntil) : '—'}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                                Este código QR es único e intransferible. Por seguridad, no lo compartas con personas desconocidas.
                            </Typography>
                        </Box>
                    </Box>

                    {!loadError && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 'var(--app-drawer-width, 0px)',
                            width: 'calc(100% - var(--app-drawer-width, 0px))',
                            px: 2,
                            pb: 3,
                            pt: 1,
                            bgcolor: 'background.default',
                            zIndex: 20
                        }}
                    >
                        <Box sx={{ maxWidth: 420, mx: 'auto' }}>
                            <Stack spacing={1.5}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    startIcon={<ShareIcon />}
                                    onClick={shareImage}
                                    sx={{ height: 56, borderRadius: '10px', fontWeight: 900 }}
                                >
                                    Compartir
                                </Button>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    startIcon={<CancelIcon />}
                                    onClick={() => setOpenConfirmDialog(true)}
                                    sx={{ height: 44, fontWeight: 800 }}
                                >
                                    Anular acceso
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                    )}

                    <Dialog
                        open={openConfirmDialog}
                        onClose={() => setOpenConfirmDialog(false)}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            {"¿Estás seguro de que quieres anular este acceso?"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Esta acción no se puede deshacer. El acceso ya no será válido después de anularlo.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
                                Cancelar
                            </Button>
                            <Button onClick={deactivateCode} color="error" autoFocus>
                                Anular
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
}